/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react-hooks/rules-of-hooks */

'use client';

import { usePathname, useSelectedLayoutSegment } from 'next/navigation';
import { useArray } from '~/utils/react';
import { UrlPath } from '~/utils/routing';
import type { Step } from '../';
import { createContext, use, useMemo } from 'react';
import type { UrlObject } from 'url';
type Steps_CtxProps<T extends Step> = {
  steps: Readonly<T[]> | T[];
};
declare namespace Steps_createCtx {
  export type Input<T extends Step> = {
    currentIdx: number;
    getStepURI: (step: T) => {
      pathname: string;
    } | {
      query: Record<string, string>;
    };
  };
}
const Steps_createCtx = <T extends Step,>(props: Steps_createCtx.Input<T> & Steps_CtxProps<T>) => {
  const state = useArray(props.steps.map(({
    ...x
  }) => ({
    ...x,
    isCompleted: false
  })));

  // prettier-ignore
  return useMemo(() => ({
    state: state.value.map((x, idx) => ({
      ...x,
      isCompleted: x.isCompleted || idx < props.currentIdx,
      idx,
      href: props.getStepURI(x)
    })),
    currentIdx: props.currentIdx,
    getStepByIdx(idx: number) {
      return this.state[idx];
    },
    getStep<P extends T['path']>(path: P) {
      const idx = state.value.findIndex(x => x.path === path)!;
      const stepState = state.getIdxState(idx)!;
      return {
        ...(stepState.value as typeof stepState.value),
        path,
        idx,
        updateState: (v: Partial<Pick<typeof stepState.value, 'isCompleted'>>) => stepState.setValue(prev => ({
          ...prev,
          isCompleted: v.isCompleted ?? prev.isCompleted
        }))
      } as const;
    }
  }) as const, [state, props.steps, props.getStepURI, props.currentIdx]);
};
type Steps_Context = ReturnType<typeof Steps_createCtx>;
const Steps_Context = createContext<Steps_Context>(null!);
export const Steps_ContextProvider_Stateless: React.FC<React.PropsWithChildren<Steps_CtxProps<Step> & Steps_createCtx.Input<Step>>> = props => {
  const value = Steps_createCtx({
    getStepURI: props.getStepURI,
    currentIdx: props.currentIdx,
    steps: props.steps
  });
  return <Steps_Context.Provider value={value}>{props.children}</Steps_Context.Provider>;
};
function createStepCtxProvider<T extends Record<string, unknown> = {}>(useStepState: (props: Steps_CtxProps<Step>) => Steps_createCtx.Input<Step>): React.FC<React.PropsWithChildren<Steps_CtxProps<Step> & T>> {
  return props => {
    const state = useStepState({
      steps: props.steps
    });
    return <Steps_ContextProvider_Stateless {...state} steps={props.steps}>
                {props.children}
            </Steps_ContextProvider_Stateless>;
  };
}
export const Steps_ContextProvider_byPathname = createStepCtxProvider<{
  basePath?: string;
}>(props => {
  const currentSegment = useSelectedLayoutSegment();
  const path = usePathname();
  return useMemo(() => {
    return {
      currentIdx: props.steps.findIndex(x => x.path === currentSegment),
      getStepURI(step) {
        return {
          pathname: new UrlPath(path).setLastSegment(step.path).toString()
        } satisfies UrlObject;
      }
    };
  }, [currentSegment, props.steps, path]);
});
export function Step_useCtx<T extends Step = Step>() {
  return use(Steps_Context) as unknown as ReturnType<typeof Steps_createCtx<T>>;
}